import { RotateCcw } from "lucide-react";
import React, { useMemo, useState } from "react";
import { CSVExportButton } from "@/components/csv-export-button";
import { ExportFilenameDialog } from "@/components/export-filename-dialog";
import { DrilldownChartControls } from "@/components/metrics/drilldown-chart-controls";
import { DrilldownChartErrorBoundary } from "@/components/metrics/drilldown-chart-error-boundary";
import { DrilldownLimitAlert } from "@/components/metrics/drilldown-limit-alert";
import { DrilldownTimeSeriesChart } from "@/components/metrics/drilldown-timeseries-chart";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import { SortableTable } from "@/components/sortable-table";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWebsiteMetricDrilldown } from "@/hooks/api/use-website-metric-drilldown";
import type { WebsitePageMetrics } from "@/hooks/api/use-website-page-metrics";
import { useTableCSVExport } from "@/hooks/use-csv-export";
import { useDrilldownChartState } from "@/hooks/use-drilldown-chart-state";
import { formatDate, formatDateForFilename } from "@/lib/date-formatting";
import type { MetricsFilters } from "@/lib/filters";
import { isNumber } from "@/lib/is-number";
import { WebpageEngagementChart } from "@/pages/webpages/webpage-explorer/webpage-engagement-chart";
import { METRIC_CONFIG, renderMetric } from "./webpage-metrics-config";

interface MetricDrilldownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  webpageUrl: string | null;
  metric: keyof WebsitePageMetrics;
  channelId?: string;
  metrics?: Partial<WebsitePageMetrics>;
  drillDownLimit?: number;
  filters?: MetricsFilters;
}

// Type for drilldown items
type DrilldownItem = {
  [key: string]: string | number | null | undefined;
};

// Compact Metric Card Component
function CompactMetricCard({
  metric,
  metrics,
  loadingStates,
}: {
  metric: keyof WebsitePageMetrics;
  metrics?: Partial<WebsitePageMetrics>;
  loadingStates?: Record<
    keyof WebsitePageMetrics,
    { isLoading: boolean; isError: boolean }
  >;
}) {
  const config = METRIC_CONFIG[metric];
  const categoryStyle = CATEGORY_STYLES[config.category];
  const metricValue = metrics?.[metric];

  // Fix: Handle 0 properly - 0 is a valid numeric value, not null
  const rawValue = typeof metricValue === "number" ? metricValue : null;
  const isLoading = loadingStates?.[metric]?.isLoading || false;

  // Check if this metric would have secondary content based on comprehensive patterns
  const hasSecondaryContent = (() => {
    // Special case: For accounts_engaged, people_engaged, and deal_contacts_engaged, if value is 0, treat as simple metric
    if (
      (metric === "accounts_engaged" ||
        metric === "people_engaged" ||
        metric === "deal_contacts_engaged") &&
      rawValue === 0
    ) {
      return false;
    }

    // Metrics with per-entity secondary values (always show these, unless value is 0 for specific metrics)
    if (
      metric === "accounts_engaged" ||
      metric === "people_engaged" ||
      metric === "deal_contacts_engaged" ||
      metric.includes("per_")
    ) {
      return true;
    }

    // Amount metrics themselves should be treated as simple metrics in drilldown cards
    // They don't have secondary content - they ARE the secondary content for count metrics
    if (metric.includes("amount")) {
      return false;
    }

    // Metrics with associated amount values - only show secondary content if amount > 0
    // Per user requirement: when amount = 0, treat as if there is no subheading
    const amountMetrics: Record<string, keyof WebsitePageMetrics> = {
      opportunities_influenced: "opportunities_influenced_amount",
      opportunities_influenced_won: "opportunities_influenced_won_amount",
      opportunities_influenced_in_cycle:
        "opportunities_influenced_in_cycle_amount",
      opportunities_created_post_engagement:
        "opportunities_created_post_engagement_amount",
      progressed_opportunities: "progressed_opportunities_amount",
    };

    const amountMetricKey = amountMetrics[metric];
    if (amountMetricKey && metrics) {
      const amountValue = metrics[amountMetricKey];
      // Only show secondary content for positive amounts (0 = no subheading per requirements)
      return typeof amountValue === "number" && amountValue > 0;
    }

    return false;
  })();

  // For simple metrics, render just the formatted primary value
  let displayValue: React.ReactNode;

  if (!hasSecondaryContent) {
    // Format the primary value directly without the flex column structure
    // Fix: Only show "—" for null/undefined, not for 0
    if (rawValue === null || rawValue === undefined) {
      // Fallback: Use the full render function but extract just the primary value
      const fullRender = config.render
        ? config.render(rawValue, { metricKey: metric, metrics })
        : renderMetric(rawValue, { metricKey: metric, metrics });

      // Extract just the primary value from the flex column structure
      if (React.isValidElement(fullRender) && fullRender.props.children) {
        const children = fullRender.props.children;
        // Get the first child (primary value) from the flex column
        displayValue = Array.isArray(children) ? children[0] : fullRender;
      } else {
        displayValue = fullRender;
      }
    } else {
      const isCurrency = metric.includes("amount");
      if (isCurrency) {
        displayValue = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(rawValue);
      } else if (metric.includes("date")) {
        displayValue = rawValue ? formatDate(String(rawValue)) : "—";
      } else {
        // This should show "0" for zero values, not "—"
        displayValue = new Intl.NumberFormat("en-US").format(rawValue);
      }
    }
  } else {
    // For compact metric cards, format values directly with appropriate font sizes
    // rather than using the full render function which has large responsive fonts
    const isCurrency = metric.includes("amount");
    const primaryFormatted =
      rawValue === null || rawValue === undefined
        ? "—"
        : isCurrency
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(rawValue)
          : new Intl.NumberFormat("en-US").format(rawValue);

    // Get secondary value if needed
    let secondaryContent: React.ReactNode = null;

    // Check for amount metrics
    const amountMetrics: Record<string, keyof WebsitePageMetrics> = {
      opportunities_influenced: "opportunities_influenced_amount",
      opportunities_influenced_won: "opportunities_influenced_won_amount",
      opportunities_influenced_in_cycle:
        "opportunities_influenced_in_cycle_amount",
      opportunities_created_post_engagement:
        "opportunities_created_post_engagement_amount",
      progressed_opportunities: "progressed_opportunities_amount",
    };

    const amountMetricKey = amountMetrics[metric];
    if (amountMetricKey && metrics && rawValue !== 0) {
      const amountValue = metrics[amountMetricKey];
      if (typeof amountValue === "number" && amountValue > 0) {
        const formattedAmount = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amountValue);
        secondaryContent = <span>{formattedAmount}</span>;
      }
    }

    // Check for per-entity metrics
    if (
      (metric === "accounts_engaged" ||
        metric === "people_engaged" ||
        metric === "deal_contacts_engaged") &&
      metrics &&
      rawValue !== 0
    ) {
      const perEntityMetrics: Record<string, keyof WebsitePageMetrics> = {
        accounts_engaged: "engagements_per_account",
        people_engaged: "engagements_per_person",
        deal_contacts_engaged: "engagements_per_deal_contact",
      };
      const perEntityKey = perEntityMetrics[metric];
      if (perEntityKey) {
        const perEntityValue = metrics[perEntityKey];
        if (typeof perEntityValue === "number") {
          const formattedValue = perEntityValue.toFixed(1);
          const label =
            metric === "accounts_engaged"
              ? perEntityValue === 1
                ? "engagement/account"
                : "engagements/account"
              : metric === "people_engaged"
                ? perEntityValue === 1
                  ? "engagement/person"
                  : "engagements/person"
                : perEntityValue === 1
                  ? "engagement/contact"
                  : "engagements/contact";

          secondaryContent = (
            <span className="inline-flex items-center gap-1">
              <span>{formattedValue}</span>
              <span className="font-normal whitespace-nowrap">{label}</span>
            </span>
          );
        }
      }
    }

    displayValue = (
      <div className="flex flex-col">
        <div className="text-lg font-semibold">{primaryFormatted}</div>
        <div className="min-h-[20px] text-sm text-muted-foreground/50">
          {secondaryContent}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-2 bg-muted/30 rounded-lg border shadow-sm animate-pulse">
        <div className="w-8 h-8 bg-muted rounded flex items-center justify-center">
          <div className="w-4 h-4 bg-muted-foreground/20 rounded"></div>
        </div>
        <div className="flex-1">
          <div className="h-6 bg-muted-foreground/20 rounded w-16"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-card rounded-lg border shadow-sm">
      {/* Category Icon */}
      <div
        className={`flex items-center justify-center w-8 h-8 rounded ${categoryStyle.color}`}
      >
        {categoryStyle.icon}
      </div>

      {/* Metric Content */}
      <div className="flex-1 min-w-0">
        {!hasSecondaryContent ? (
          <div className="flex items-center text-lg font-semibold">
            {displayValue}
          </div>
        ) : (
          displayValue
        )}
      </div>
    </div>
  );
}

export function MetricDrilldown({
  open,
  onOpenChange,
  webpageUrl,
  metric,
  channelId,
  metrics,
  filters,
  drillDownLimit,
}: MetricDrilldownProps) {
  const metricConfig = METRIC_CONFIG[metric];
  const [resetTrigger, setResetTrigger] = useState<number | undefined>(
    undefined,
  );
  // Show the reset button for any drill-down that renders a table (i.e., anything except "chart")
  const showResetButton = metricConfig?.drilldownType !== "chart";

  // Map frontend metric names to backend API metric names
  const apiMetricName = metric;

  const metricValue = metrics?.[metric];
  // Skip limit check for amount/currency metrics since they show dollar values, not row counts
  const isAmountMetric = metric.includes("amount");
  const isDrillDownEnabled =
    isNumber(drillDownLimit) && isNumber(metricValue) && !isAmountMetric
      ? metricValue < drillDownLimit
      : true;

  const { data: drilldownResponse, isLoading } = useWebsiteMetricDrilldown({
    url: webpageUrl || "",
    metric: apiMetricName,
    channelId,
    limit: 1000,
    filters,
    enabled: open && !!webpageUrl && !!metric && isDrillDownEnabled,
  });

  const drilldownData = useMemo(
    () => drilldownResponse?.results || [],
    [drilldownResponse],
  );

  const {
    viewMode,
    setViewMode,
    cumulative,
    setCumulative,
    dateColumns,
    typedRows: typedDrilldownRows,
    hasDateColumns,
    showChartToggle,
    grouping,
    handleGroupingChange,
  } = useDrilldownChartState({
    metricKey: metric,
    drilldownColumns: metricConfig?.drilldownColumns,
    drilldownType: metricConfig?.drilldownType,
    rows: drilldownData,
  });
  // Setup CSV export
  const { isExporting, exportData, defaultFilename } =
    useTableCSVExport<DrilldownItem>({
      data: drilldownData as unknown as DrilldownItem[],
      columns: metricConfig?.drilldownColumns || [],
      filename: `${webpageUrl}-${metric}-${formatDateForFilename(new Date())}`,
    });

  // State for export filename dialog
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const handleExport = (filename: string) => {
    exportData(filename);
  };

  // Check if we have exportable data
  const hasExportableData = useMemo(() => {
    return (
      metricConfig?.drilldownType === "table" &&
      drilldownData &&
      Array.isArray(drilldownData) &&
      drilldownData.length > 0
    );
  }, [metricConfig?.drilldownType, drilldownData]);

  if (!metricConfig || !webpageUrl) {
    return null;
  }

  const tableData = (drilldownData ?? []) as unknown as DrilldownItem[];

  // Reset handler
  const handleResetTable = () => {
    setResetTrigger((prev) => (prev ?? -1) + 1);
  };

  function renderContent() {
    if (!isDrillDownEnabled && drillDownLimit) {
      return (
        <div className="flex justify-center items-center p-4 py-12">
          <DrilldownLimitAlert limit={drillDownLimit} />
        </div>
      );
    }

    if (!metricConfig || metricConfig.drilldownColumns.length === 0) {
      return (
        <div className="p-4 py-12 text-center">
          <p className="text-lg text-muted-foreground">
            Drilldown is not available for this metric.
          </p>
        </div>
      );
    }

    if (metricConfig.drilldownType === "chart") {
      // Only pass the metric if it's one of the valid engagement metrics
      const validMetric =
        metric === "days_since_first_engagement" ||
        metric === "days_since_latest_engagement" ||
        metric === "webpage_active_days"
          ? metric
          : undefined;

      return (
        <div className="p-4">
          <WebpageEngagementChart
            webpageUrl={webpageUrl}
            metric={validMetric}
            channelId={channelId}
          />
        </div>
      );
    }

    // Chart mode for table-type metrics with date columns
    if (viewMode === "chart" && hasDateColumns && drilldownData) {
      return (
        <div className="h-full flex flex-col bg-card border border-border rounded-lg">
          <DrilldownChartErrorBoundary>
            <DrilldownTimeSeriesChart
              data={typedDrilldownRows}
              columns={dateColumns}
              grouping={grouping}
              cumulative={cumulative}
            />
          </DrilldownChartErrorBoundary>
        </div>
      );
    }

    return (
      <div className="max-h-[calc(80vh-14rem)] overflow-y-auto bg-card border border-border rounded-lg">
        <SortableTable<DrilldownItem>
          tableName={`webpages::drilldown::${metric}`}
          columns={metricConfig.drilldownColumns}
          data={tableData}
          keyExtractor={(item) => {
            const typedItem = item as Record<string, string | number>;
            return metricConfig.primaryKey
              .map((key) => typedItem[key] ?? "")
              .join("-");
          }}
          isLoading={isLoading}
          defaultSort={metricConfig.defaultSort}
          resetTrigger={resetTrigger}
          disableInternalScrolling={true}
          headerClassName="-top-px"
        />
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[90vw] max-w-full max-h-[90vh] flex flex-col overflow-hidden"
        onOpenAutoFocus={(e) => {
          // Prevent the default behavior of focusing the first focusable element
          e.preventDefault();
        }}
      >
        <DialogHeader className="flex flex-row items-end justify-between pb-2 space-y-0 pr-0">
          <div className="flex flex-col gap-3 flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-center gap-3">
              <DialogTitle className="text-xl font-semibold">
                {metricConfig.title}
              </DialogTitle>
              {/* Compact Metric Card */}
              <CompactMetricCard metric={metric} metrics={metrics} />
            </div>

            {/* Tooltip Description */}
            {metricConfig.tooltip && (
              <p className="text-sm text-muted-foreground">
                {metricConfig.tooltip}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <DrilldownChartControls
              grouping={grouping}
              onGroupingChange={handleGroupingChange}
              cumulative={cumulative}
              onCumulativeChange={setCumulative}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showChartControls={viewMode === "chart" && hasDateColumns}
              showChartToggle={showChartToggle}
            />

            {/* Reset Table Button (only for table drill-downs) */}
            {showResetButton && viewMode === "table" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleResetTable}
                    className="h-8 w-8 bg-card"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reset table to default state</p>
                </TooltipContent>
              </Tooltip>
            )}
            {hasExportableData && (
              <ExportFilenameDialog
                open={isExportDialogOpen}
                onOpenChange={setIsExportDialogOpen}
                defaultFilename={defaultFilename}
                extension="csv"
                onExport={handleExport}
                disabled={isExporting}
              >
                <CSVExportButton
                  onClick={() => setIsExportDialogOpen(true)}
                  isExporting={isExporting}
                  tooltipText="Export as CSV"
                  disabled={isExporting}
                  className="bg-card"
                />
              </ExportFilenameDialog>
            )}
          </div>
        </DialogHeader>

        <div className="flex-grow rounded-md border">{renderContent()}</div>
      </DialogContent>
    </Dialog>
  );
}
